import { useQuery } from '@tanstack/react-query';
import { Blocks, Boxes, Download } from 'lucide-react';
import WidgetShell from '@/components/admin/dashboard/WidgetShell';
import StatCard from '@/components/admin/dashboard/StatCard';
import api from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Apps Platform summary. GET /admin/apps lists every app registered on this
 * tenant plus a live `installation_count` (non-revoked app_installations per
 * app). There is no dedicated per-app engagement/analytics endpoint, so this
 * widget is a status roll-up (registered apps + active installs + per-app
 * install counts), linking to /admin/apps for management.
 */

interface AppRow {
  id: string;
  name: string;
  icon_url: string | null;
  is_active: boolean;
  installation_count: number;
}

interface AppsResponse {
  apps: AppRow[];
}

export default function AppEngagementWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'apps'],
    queryFn: () => api.get<AppsResponse>('/admin/apps'),
    staleTime: 60000,
  });

  const apps = data?.apps ?? [];
  const activeApps = apps.filter((a) => a.is_active).length;
  const totalInstalls = apps.reduce((sum, a) => sum + (a.installation_count ?? 0), 0);

  const topApps = [...apps]
    .sort((a, b) => b.installation_count - a.installation_count)
    .slice(0, 4);

  return (
    <WidgetShell
      title="Apps"
      icon={<Blocks />}
      to="/admin/apps"
      linkLabel="Manage apps"
      isLoading={isLoading}
      error={error}
      isEmpty={!!data && apps.length === 0}
      emptyMessage="No apps registered yet."
    >
      {data && apps.length > 0 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <StatCard
              title="Active apps"
              value={activeApps}
              icon={<Boxes />}
              tone="blue"
              subtitle={`${apps.length} registered`}
            />
            <StatCard
              title="Installations"
              value={totalInstalls.toLocaleString()}
              icon={<Download />}
              tone="teal"
            />
          </div>

          <ul className="divide-y divide-line">
            {topApps.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-3 py-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className={cn(
                      'h-2 w-2 shrink-0 rounded-full',
                      a.is_active ? 'bg-green-500' : 'bg-stone-300',
                    )}
                    aria-hidden="true"
                  />
                  <p className="truncate text-sm font-medium text-ink">{a.name}</p>
                </div>
                <span className="shrink-0 text-xs text-stone-500">
                  {a.installation_count.toLocaleString()} installed
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </WidgetShell>
  );
}
